import {AccentColorOptions} from 'epic-ue-theme';
import React, {useCallback} from 'react';
import styled, {createGlobalStyle} from 'styled-components';
import {RemoveIcon} from '..';

export const LightboxOverrides = createGlobalStyle`
    body {
        overflow: hidden;
    }

    /* twinmotion overrides */
    #twinmotionApp {
        overflow: hidden;
        .home-page {
            overflow: hidden;
            max-height: 100vh;
        }
    }

    /* some overrides to fix for being nested inside of a position: relative parent */
    .ue-app {
        .case-study {
            .content, .content .content-inner {
                position: static;
            }
        }
        .ue-section-intro {
            position: static;
        }
    }
`;

const LightboxOverlay = styled.div<{accentColor?: AccentColorOptions}>`
    z-index: 2000;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 0;
    height: 0;
    overflow: hidden;
    display: none;
    &.lightbox-open {
        display: block;
        width: 100vw;
        height: 100vh;
    }
    background: ${(p) =>
        p.theme.ifLight(p, p.theme.palette.neutral.stone, p.theme.palette.neutral.charcoal)};
    opacity: 1;

    button {
        color: ${(p) => p.theme.palette.text.primary};
        background: none;
        position: absolute;
        cursor: pointer;
        top: 32px;
        right: 32px;
        border: 0;
        outline: none;
        font-size: 40px;
        line-height: 32px;
        padding: 0;
        margin: 0;
        transition: color 0.25s ${(p) => p.theme.easing};
        ${(p) => p.theme.breakpoints.down('md')} {
            top: 12px;
            right: 12px;
        }
        svg {
            font-size: inherit;
            cursor: pointer;
        }

        &:hover {
            color: ${(p) =>
                p.accentColor
                    ? p.theme.palette.accent[p.accentColor]
                    : p.theme.palette.text.primary};
        }
    }
`;

const LightboxWrapper = styled.div`
    width: 75vw;
    ${(p) => p.theme.breakpoints.down('md')} {
        width: 85vw;
    }
    ${(p) => p.theme.breakpoints.down('sm')} {
        width: 90vw;
    }
    margin: auto;
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: 2001;
    opacity: 0;
    visibility: hidden;
    height: 0;
    &.lightbox-open {
        opacity: 1;
        visibility: visible;
        height: 75vh;
        ${(p) => p.theme.breakpoints.down('md')} {
            height: 85vh;
        }
    }

    .lightbox-content {
        height: 100%;
        width: 100%;
    }
`;

const CloseButton = styled.button<{name: string}>`
    color: ${(p) => p.theme.palette.text.primary};
    background: none;
    position: absolute;
    right: 45px;
    border: 0;
    outline: none;
    font-size: 1.2em;
    padding: 0;
    margin: 0;
    cursor: pointer;
    ${(p) => p.theme.breakpoints.down('sm')} {
        font-size: 1.5em;
    }

    svg {
        transition: color 0.25s ${(p) => p.theme.easing}, opacity 0.25s ${(p) => p.theme.easing},
            transform 0.25s ${(p) => p.theme.easing};
    }

    &:focus,
    &:hover {
        svg {
            color: ${(p) => p.theme.palette.accent[p.name]};
        }
    }

    &:active {
        svg {
            opacity: 0.5;
            transform: scale(0.8);
        }
    }
`;

type ClickCloseHandler = (index: null) => void;
export interface UeLightboxProps {
    isOpen: boolean;
    children: React.ReactNode;
    onClick: ClickCloseHandler;
    accentColor?: AccentColorOptions;
}

export const UeLightbox = ({
    isOpen,
    children,
    onClick,
    accentColor = 'sky'
}: UeLightboxProps): JSX.Element => {
    const lightboxClose = useCallback(
        (e) => {
            if (onClick && typeof onClick === 'function') {
                onClick(null);
            }
        },
        [onClick]
    );

    return (
        <>
            {isOpen && <LightboxOverrides />}
            <LightboxOverlay
                className={`overlay ${isOpen ? 'lightbox-open' : ''}`}
                accentColor={accentColor}>
                <CloseButton onClick={lightboxClose} name={accentColor}>
                    <RemoveIcon />
                </CloseButton>
            </LightboxOverlay>
            <LightboxWrapper className={`${isOpen ? 'lightbox-open' : ''}`}>
                <div className="lightbox-content">{isOpen ? children : null}</div>
            </LightboxWrapper>
        </>
    );
};
